import React, { useState, useEffect } from "react";
import axios from "axios";
import { getApiUrl } from "../../../../config/api";
import { useAuth } from "../../../../firebase/AuthContext";

// The owner's view of a bot's visitor sessions, behind the
// webchatbot.conversations.* roles. The widget itself uses the public
// /api/bot-widget routes for a visitor's own session.
const BOT_API = `${getApiUrl()}/api/bot-dashboard`;
const authHeader = (token) => ({ Authorization: `Bearer ${token}` });

const Spinner = () => (
  <div className="flex justify-center py-8">
    <svg
      className="animate-spin h-8 w-8 text-[#E78A24]"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  </div>
);

const PAGE_SIZE = 20;

const ConversationsTab = ({ bot, token, isActive }) => {
  const { hasRole } = useAuth();
  const canManage = hasRole("webchatbot.conversations.manage");
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openSession, setOpenSession] = useState(null);
  const [sessionMessages, setSessionMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [editingTitle, setEditingTitle] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const editingValueRef = React.useRef("");

  // Pagination
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  });

  const fetchSessions = async (targetPage = 1) => {
    setLoading(true);
    try {
      const res = await axios.get(`${BOT_API}/${bot.bot_id}/conversations`, {
        params: { page: targetPage, limit: PAGE_SIZE },
        headers: authHeader(token),
      });
      if (res.data.success) {
        setSessions(res.data.sessions || []);
        setPage(res.data.page || targetPage);
        setMeta({
          total: res.data.total ?? (res.data.sessions?.length || 0),
          totalPages: res.data.total_pages ?? 1,
          hasNext: res.data.has_next ?? false,
          hasPrev: res.data.has_prev ?? false,
        });
        setOpenSession(null); // collapse any expanded row when the list changes
      }
    } catch (err) {
      console.error("[BotDashboard] Failed to fetch sessions:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isActive) fetchSessions(1);
  }, [bot.bot_id, isActive]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRenameStart = (e, s) => {
    e.stopPropagation();
    if (!canManage) return;
    editingValueRef.current = s.title || s.user_preview || "";
    setEditingTitle(s.session_id);
    setEditingValue(s.title || s.user_preview || "");
  };

  const handleRenameCommit = async (sessionId) => {
    const newTitle = editingValueRef.current.trim();
    setEditingTitle(null);
    setEditingValue("");
    editingValueRef.current = "";
    if (!newTitle) return;

    const prevSessions = sessions;
    setSessions((prev) =>
      prev.map((s) =>
        s.session_id === sessionId ? { ...s, title: newTitle } : s,
      ),
    );
    try {
      await axios.patch(
        `${BOT_API}/${bot.bot_id}/conversations/${sessionId}`,
        { title: newTitle },
        { headers: authHeader(token) },
      );
    } catch (err) {
      console.error("[BotDashboard] Failed to rename session:", err);
      setSessions(prevSessions);
    }
  };

  const handleOpenSession = async (sessionId) => {
    if (openSession === sessionId) {
      setOpenSession(null);
      return;
    }
    setOpenSession(sessionId);
    setLoadingMessages(true);
    try {
      const res = await axios.get(
        `${BOT_API}/${bot.bot_id}/conversations/${sessionId}`,
        { headers: authHeader(token) },
      );
      if (res.data.success) {
        const msgs = Object.values(res.data.messages || {}).sort(
          (a, b) => a.timestamp - b.timestamp,
        );
        setSessionMessages(msgs);
      }
    } catch (err) {
      console.error("[BotDashboard] Failed to load session messages:", err);
    } finally {
      setLoadingMessages(false);
    }
  };

  if (loading)
    return (
      <div className="flex flex-1 w-full h-full items-center justify-center">
        <Spinner />
      </div>
    );

  if (!sessions.length) {
    return (
      <div className="text-center py-12 text-gray-400">
        <p className="text-sm">No conversations yet.</p>
        <p className="text-xs mt-1">
          Conversations will appear here once users start chatting.
        </p>
        <button
          onClick={() => fetchSessions(1)}
          className="mt-3 px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition-colors"
        >
          Refresh
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          {meta.total} conversation{meta.total !== 1 ? "s" : ""} recorded
        </p>
        <button
          onClick={() => fetchSessions(page)}
          className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition-colors"
        >
          Refresh
        </button>
      </div>
      {sessions.map((s) => (
        <div
          key={s.session_id}
          className="border border-gray-200 rounded-xl overflow-hidden"
        >
          <div
            onClick={() => handleOpenSession(s.session_id)}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer bg-white"
          >
            <div className="flex-1 min-w-0">
              {editingTitle === s.session_id ? (
                <input
                  autoFocus
                  className="text-sm font-medium text-gray-800 border-b border-[#E78A24] outline-none bg-transparent w-full"
                  value={editingValue}
                  onChange={(e) => {
                    editingValueRef.current = e.target.value;
                    setEditingValue(e.target.value);
                  }}
                  onBlur={() => handleRenameCommit(s.session_id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleRenameCommit(s.session_id);
                    }
                    if (e.key === "Escape") {
                      setEditingTitle(null);
                      setEditingValue("");
                      editingValueRef.current = "";
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <div className="flex items-center gap-1.5">
                  <p
                    className="text-sm font-medium text-gray-800 truncate"
                    onDoubleClick={(e) => handleRenameStart(e, s)}
                  >
                    {s.title || s.user_preview || "Chat Session"}
                  </p>
                  {canManage && (
                  <button
                    onClick={(e) => handleRenameStart(e, s)}
                    title="Rename"
                    className="text-gray-300 hover:text-gray-500 transition-colors shrink-0"
                  >
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15.232 5.232l3.536 3.536M9 13l6.586-6.586a2 2 0 112.828 2.828L11.828 15.828a2 2 0 01-1.414.586H8v-2.414a2 2 0 01.586-1.414z"
                      />
                    </svg>
                  </button>
                  )}
                </div>
              )}
              <p className="text-xs text-gray-400 mt-0.5">
                {s.created_at
                  ? new Date(s.created_at).toLocaleString()
                  : "No date"}
                {s.updated_at && s.updated_at !== s.created_at
                  ? ` · Updated ${new Date(s.updated_at).toLocaleString()}`
                  : ""}
                {" · "}
                {s.message_count} msgs
              </p>
              {(s.visitor_ip || s.referrer_url) && (
                <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-2 flex-wrap">
                  {s.visitor_ip && (
                    <span title="Visitor IP">
                      <span className="text-gray-300 mr-0.5">IP</span>
                      {s.visitor_ip}
                    </span>
                  )}
                  {s.referrer_url && (
                    <span
                      className="truncate max-w-[260px]"
                      title={s.referrer_url}
                    >
                      <span className="text-gray-300 mr-0.5">from</span>
                      {s.referrer_url}
                    </span>
                  )}
                </p>
              )}
            </div>
            <svg
              className={`w-4 h-4 text-gray-400 transition-transform shrink-0 ml-2 ${openSession === s.session_id ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>

          {openSession === s.session_id && (
            <div className="border-t border-gray-100 px-4 py-3 space-y-2 bg-gray-50">
              {loadingMessages ? (
                <Spinner />
              ) : (
                sessionMessages.map((msg, i) => (
                  <div
                    key={i}
                    className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${
                        msg.role === "user"
                          ? "bg-[#F8EDD8] text-gray-800"
                          : "bg-white border border-gray-200 text-gray-700"
                      }`}
                    >
                      <p>{msg.message}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(msg.timestamp).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      ))}

      {meta.totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <button
            onClick={() => fetchSessions(page - 1)}
            disabled={!meta.hasPrev || loading}
            className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            ← Previous
          </button>
          <span className="text-xs text-gray-500">
            Page {page} of {meta.totalPages}
          </span>
          <button
            onClick={() => fetchSessions(page + 1)}
            disabled={!meta.hasNext || loading}
            className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default ConversationsTab;
